import type { QueryClient } from "@tanstack/react-query";
import { createBrowserRouter, createMemoryRouter, type RouteObject } from "react-router";
import Empty from "./routes/empty";
import ErrorRoute from "./routes/error";
import { createElement } from "react";

const getRouter = (queryClient: QueryClient) =>
  [
    {
      HydrateFallback: Empty,
      errorElement: createElement(ErrorRoute),
      lazy: async () => {
        const Root = await import("./routes/root");
        return {
          Component: Root.default,
        };
      },
      children: [
        {
          handle: {
            level: 0,
          },
          lazy: async () => {
            const RootLayout = await import("./routes/layouts/root-layout");
            return { Component: RootLayout.default };
          },
          children: [
            {
              path: "/",
              lazy: async () => {
                (await import("@/api/home/prefetch")).default(queryClient);
                const Home = await import("@/routes/home");

                return {
                  Component: Home.default,
                };
              },
            },
            {
              path: "live",
              lazy: async () => {
                (await import("@/api/live/prefetch")).default(queryClient);
                const LiveList = await import("@/routes/lives/live-list");
                return { Component: LiveList.default };
              },
            },
            {
              path: "events",
              lazy: async () => {
                (await import("@/api/events/prefetch")).default(queryClient);
                const event = await import("@/routes/events/event-list");

                return { Component: event.default };
              },
            },
            {
              path: "posts",
              lazy: async () => {
                (await import("@/api/posts/prefetch")).default(queryClient);
                const PostsLayout = await import("@/routes/layouts/posts-layout");

                return { Component: PostsLayout.default };
              },
              children: [
                {
                  path: ":categoryKey?",
                  lazy: async () => {
                    const PostList = await import("@/routes/posts/post-list");
                    return {
                      Component: PostList.default,
                    };
                  },
                },
              ],
            },
            {
              path: "chat",
              lazy: async () => {
                const Chat = await import("@/routes/chat/chat");
                return { Component: Chat.default };
              },
            },
            {
              lazy: async () => {
                const AuthGuard = await import("@/routes/layouts/auth-guard");
                return { Component: AuthGuard.default };
              },
              children: [
                {
                  path: "profile",
                  lazy: async () => {
                    const Profile = await import("@/routes/profile/profile");
                    return { Component: Profile.default };
                  },
                },
              ],
            },
          ],
        },
        {
          handle: {
            level: 1,
          },
          lazy: async () => {
            const StackLayout = await import("@/routes/layouts/stack-layout");
            return { Component: StackLayout.default };
          },
          children: [
            {
              path: "/events/:eventId",
              lazy: async () => {
                const EventRoom = await import("@/routes/events/event-room");
                return {
                  Component: EventRoom.default,
                  loader: async ({ params }) => {
                    if (!params.eventId) return;
                    (await import("@/api/eventId/prefetch")).default(queryClient, +params.eventId);
                  },
                };
              },
            },
            {
              path: "/live/:liveId",
              lazy: async () => {
                const LiveRoom = await import("@/routes/lives/live-room");
                return {
                  Component: LiveRoom.default,
                  loader: async ({ params }) => {
                    if (!params.liveId) return;
                    (await import("@/api/live/prefetch")).prefetchLiveRoom(queryClient, +params.liveId);
                  },
                };
              },
            },
            {
              path: "/posts/:categoryKey/:postId",
              lazy: async () => {
                const Post = await import("@/routes/posts/post");
                return {
                  Component: Post.default,
                };
              },
            },
            {
              lazy: async () => {
                const AuthGuard = await import("@/routes/layouts/auth-guard");
                return { Component: AuthGuard.default };
              },
              children: [
                {
                  path: "/profile/qr-code",
                  lazy: async () => {
                    const MyQRCode = await import("@/routes/profile/qr-code");
                    return { Component: MyQRCode.default };
                  },
                },
                {
                  path: "/profile/info",
                  lazy: async () => {
                    const UserInfo = await import("@/routes/profile/user-info");
                    return { Component: UserInfo.default };
                  },
                },
                {
                  handle: {
                    level: 2,
                  },
                  path: "/profile/info/nickName",
                  lazy: async () => {
                    const NickName = await import("@/routes/profile/user-info/nick-name");
                    return { Component: NickName.default };
                  },
                },
                {
                  handle: {
                    level: 2,
                  },
                  path: "/profile/info/password",
                  lazy: async () => {
                    const Password = await import("@/routes/profile/user-info/password");
                    return { Component: Password.default };
                  },
                },
                {
                  path: "/profile/wallet",
                  lazy: async () => {
                    const Wallet = await import("@/routes/profile/wallet");
                    return { Component: Wallet.default };
                  },
                },
                {
                  path: "/profile/missions",
                  lazy: async () => {
                    const Missions = await import("@/routes/profile/missions");
                    return { Component: Missions.default };
                  },
                },
                {
                  path: "/profile/bets",
                  lazy: async () => {
                    const MyBets = await import("@/routes/profile/bets");
                    return { Component: MyBets.default };
                  },
                },
                {
                  path: "/profile/events",
                  lazy: async () => {
                    const Events = await import("@/routes/profile/events");
                    return { Component: Events.default };
                  },
                },
                {
                  path: "/profile/streamers",
                  lazy: async () => {
                    const Streamers = await import("@/routes/profile/streamers");
                    return { Component: Streamers.default };
                  },
                },
                {
                  path: "/profile/collection",
                  lazy: async () => {
                    const MyCollections = await import("@/routes/profile/my-collections");
                    return { Component: MyCollections.default };
                  },
                },
                {
                  path: "/profile/notification",
                  lazy: async () => {
                    const Notification = await import("@/routes/profile/notification");
                    return { Component: Notification.default };
                  },
                },
                {
                  path: "/profile/message",
                  lazy: async () => {
                    const MessagingSettings = await import("@/routes/profile/messaging-settings");
                    return { Component: MessagingSettings.default };
                  },
                },
                {
                  path: "/profile/privacy",
                  lazy: async () => {
                    const PrivacySettings = await import("@/routes/profile/privacy-settings");
                    return { Component: PrivacySettings.default };
                  },
                },
                {
                  path: "/profile/general",
                  lazy: async () => {
                    const General = await import("@/routes/profile/general");
                    return { Component: General.default };
                  },
                },
                {
                  handle: {
                    level: 2,
                  },
                  path: "/profile/general/language",
                  lazy: async () => {
                    const Language = await import("@/routes/profile/general/language");
                    return { Component: Language.default };
                  },
                },
                {
                  handle: {
                    level: 2,
                  },
                  path: "/profile/general/about",
                  lazy: async () => {
                    const About = await import("@/routes/profile/general/about");
                    return { Component: About.default };
                  },
                },
              ],
            },
            {
              path: "/sign-up/agreement",
              handle: {
                level: 1000,
                vertical: true,
              },
              lazy: async () => {
                const Agreement = await import("@/routes/register/agreement");
                return { Component: Agreement.default };
              },
            },
            {
              handle: {
                level: 999,
                vertical: true,
              },
              lazy: async () => {
                const RegisterLayout = await import("@/routes/layouts/register-layout");
                return { Component: RegisterLayout.default };
              },
              children: [
                {
                  path: "/sign-up",
                  lazy: async () => {
                    const SignUp = await import("@/routes/register/sign-up");
                    return { Component: SignUp.default };
                  },
                },
                {
                  path: "/sign-in",
                  lazy: async () => {
                    const SignIn = await import("@/routes/register/sign-in");
                    return { Component: SignIn.default };
                  },
                },
                {
                  path: "/forgot-password",
                  lazy: async () => {
                    const ForgotPassword = await import("@/routes/register/forgot-password");
                    return { Component: ForgotPassword.default };
                  },
                },
              ],
            },
          ],
        },
        {
          handle: {
            level: 999999,
            vertical: true,
          },
          path: "download",
          lazy: async () => {
            const Download = await import("@/routes/download");
            return { Component: Download.default };
          },
        },
      ],
    },
  ] as RouteObject[];

export const browserRouter = (queryClient: QueryClient) => createBrowserRouter(getRouter(queryClient));

export const memoryRouter = (queryClient: QueryClient) => createMemoryRouter(getRouter(queryClient));
